import React, { useState } from 'react';
import { X } from 'lucide-react';
import { DocumentCategory } from '../types';

interface Props {
  categories: DocumentCategory[];
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
}

export const DocumentCreateModal: React.FC<Props> = ({
  categories,
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [documentNumber, setDocumentNumber] = useState('');
  const [title, setTitle] = useState('');
  const [categoryId, setCategoryId] = useState(categories[0]?.id || 'CAT-MANUAL');
  const [department, setDepartment] = useState('Flight Operations');
  const [ownerName, setOwnerName] = useState('Kaptan Burak Demir');
  const [reviewMonths, setReviewMonths] = useState(12);
  const [requiresAck, setRequiresAck] = useState(true);
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await onSubmit({
        document_number: documentNumber,
        title,
        category_id: categoryId,
        department,
        owner_id: 'USR-FO-01',
        owner_name: ownerName,
        review_cycle_months: Number(reviewMonths),
        requires_acknowledgement: requiresAck,
        description,
        initial_version_number: '1.0',
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Hata olustu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card">
        <div className="modal-header">
          <div className="modal-header-content">
            <h3>Create Controlled Document</h3>
            <p>Publish a new regulatory or operational controlled document</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="modal-close-btn"
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {error && (
              <div className="login-error-alert">
                {error}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              <div className="form-field">
                <label>Document No *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. OM-A-REV06"
                  value={documentNumber}
                  onChange={(e) => setDocumentNumber(e.target.value)}
                />
              </div>

              <div className="form-field">
                <label>Category *</label>
                <select
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                >
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name} ({cat.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-field">
              <label>Document Title *</label>
              <input
                type="text"
                required
                placeholder="e.g. Flight Operations Safety & Compliance Manual"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              <div className="form-field">
                <label>Department *</label>
                <select
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                >
                  <option value="Flight Operations">Flight Operations</option>
                  <option value="Ground Operations">Ground Operations</option>
                  <option value="Safety & Quality">Safety & Quality (SMS/QA)</option>
                  <option value="Maintenance / CAMO">Maintenance / CAMO</option>
                  <option value="Security">Security (AVSEC)</option>
                </select>
              </div>

              <div className="form-field">
                <label>Review Cycle (Months)</label>
                <input
                  type="number"
                  min="1"
                  max="36"
                  value={reviewMonths}
                  onChange={(e) => setReviewMonths(Number(e.target.value))}
                />
              </div>
            </div>

            <div className="form-field">
              <label>Document Author / Owner *</label>
              <input
                type="text"
                required
                value={ownerName}
                onChange={(e) => setOwnerName(e.target.value)}
              />
            </div>

            <div className="form-field">
              <label>Scope & Purpose Description</label>
              <textarea
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Operational purpose, applicability, and safety scope..."
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  fontSize: '13px',
                  fontFamily: 'inherit',
                }}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '4px' }}>
              <input
                type="checkbox"
                id="reqAck"
                checked={requiresAck}
                onChange={(e) => setRequiresAck(e.target.checked)}
                style={{ width: 'auto', cursor: 'pointer' }}
              />
              <label htmlFor="reqAck" style={{ fontSize: '13px', color: '#334155', cursor: 'pointer' }}>
                Requires mandatory employee acknowledgement upon publication
              </label>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              style={{ width: 'auto' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
              style={{ width: 'auto' }}
            >
              {loading ? 'Saving...' : 'Create Document'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
